import type { ResumeData, ResumeSettings, TemplateType } from '../types/resume';
import { createElement, type ComponentType } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { ModernTemplate } from '../templates/ModernTemplate';
import { ClassicTemplate } from '../templates/ClassicTemplate';
import { ProfessionalTemplate } from '../templates/ProfessionalTemplate';
import { ExecutiveTemplate } from '../templates/ExecutiveTemplate';
import { MinimalTemplate } from '../templates/MinimalTemplate';
import { TechnicalTemplate } from '../templates/TechnicalTemplate';
import { PortfolioTemplate } from '../templates/PortfolioTemplate';
import { ElegantTemplate } from '../templates/ElegantTemplate';
import { CompactTemplate } from '../templates/CompactTemplate';
import { SidebarRightTemplate } from '../templates/SidebarRightTemplate';
import { AtsTemplate } from '../templates/AtsTemplate';
import { TwoColumnTemplate } from '../templates/TwoColumnTemplate';
import { AcademicTemplate } from '../templates/AcademicTemplate';
import { CreativeTemplate } from '../templates/CreativeTemplate';
import { StandardTemplate } from '../templates/StandardTemplate';
import { PhotoTemplate } from '../templates/PhotoTemplate';
import { CleanTwoColumnTemplate } from '../templates/CleanTwoColumnTemplate';
import { ModernSidebarTemplate } from '../templates/ModernSidebarTemplate';
import {
  AtsCompactTemplate,
  AtsSerifTemplate,
  BoardroomTemplate,
  CloudTemplate,
  CorporateBlueTemplate,
  CybersecurityTemplate,
  DataTemplate,
  DevOpsTemplate,
  EngineerTemplate,
  NetworkTemplate,
} from '../templates/CareerTemplates';
import { mergeSettings } from './resume-settings';

const TEMPLATE_COMPONENTS: Record<TemplateType, ComponentType<{ data: ResumeData; settings?: ResumeSettings }>> = {
  modern: ModernTemplate,
  classic: ClassicTemplate,
  professional: ProfessionalTemplate,
  executive: ExecutiveTemplate,
  minimal: MinimalTemplate,
  technical: TechnicalTemplate,
  portfolio: PortfolioTemplate,
  elegant: ElegantTemplate,
  compact: CompactTemplate,
  'sidebar-right': SidebarRightTemplate,
  ats: AtsTemplate,
  'two-column': TwoColumnTemplate,
  academic: AcademicTemplate,
  creative: CreativeTemplate,
  standard: StandardTemplate,
  'ats-compact': AtsCompactTemplate,
  'ats-serif': AtsSerifTemplate,
  'corporate-blue': CorporateBlueTemplate,
  boardroom: BoardroomTemplate,
  engineer: EngineerTemplate,
  devops: DevOpsTemplate,
  cybersecurity: CybersecurityTemplate,
  network: NetworkTemplate,
  data: DataTemplate,
  cloud: CloudTemplate,
  photo: PhotoTemplate,
  'clean-two-column': CleanTwoColumnTemplate,
  'modern-sidebar': ModernSidebarTemplate,
};

export type PrintLayout = '1-page' | 'standard' | 'compact';

/**
 * Layout-specific CSS that ACTUALLY reduces font sizes and spacing.
 * transform:scale() doesn't reduce page count — we need real size reduction.
 */
const LAYOUT_CSS: Record<PrintLayout, string> = {
  '1-page': `
    :root {
      --fs-name: 1.75rem;
      --fs-h2: 0.7rem;
      --fs-h3: 0.8rem;
      --fs-body: 0.7rem;
      --fs-small: 0.6rem;
      --fs-xs: 0.55rem;
      --line-height: 1.35;
      --section-gap: 0.75rem;
      --header-pad-y: 0.75rem;
      --header-pad-x: 1.5rem;
      --content-pad: 1rem 1.5rem;
      --item-gap: 0.4rem;
      --skill-pad: 0.2rem 0.5rem;
      --tag-pad: 0.1rem 0.4rem;
    }
  `,
  'standard': `
    :root {
      --fs-name: 2.25rem;
      --fs-h2: 0.875rem;
      --fs-h3: 1rem;
      --fs-body: 0.875rem;
      --fs-small: 0.75rem;
      --fs-xs: 0.625rem;
      --line-height: 1.6;
      --section-gap: 1.5rem;
      --header-pad-y: 1.5rem;
      --header-pad-x: 2.5rem;
      --content-pad: 1.5rem 2.5rem;
      --item-gap: 0.75rem;
      --skill-pad: 0.375rem 0.75rem;
      --tag-pad: 0.125rem 0.5rem;
    }
  `,
  'compact': `
    :root {
      --fs-name: 2rem;
      --fs-h2: 0.8rem;
      --fs-h3: 0.9rem;
      --fs-body: 0.8rem;
      --fs-small: 0.7rem;
      --fs-xs: 0.6rem;
      --line-height: 1.5;
      --section-gap: 1rem;
      --header-pad-y: 1rem;
      --header-pad-x: 2rem;
      --content-pad: 1.25rem 2rem;
      --item-gap: 0.5rem;
      --skill-pad: 0.3rem 0.6rem;
      --tag-pad: 0.125rem 0.4rem;
    }
  `,
};

export function printResume(data: ResumeData, template: TemplateType, layout: PrintLayout = 'standard', settings?: ResumeSettings) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups to print/save your resume as PDF');
    return;
  }

  const s = mergeSettings(settings);
  const Component = TEMPLATE_COMPONENTS[template] || ModernTemplate;
  const html = renderToStaticMarkup(createElement(Component, { data, settings: s }));

  const appStyles = Array.from(document.querySelectorAll<HTMLLinkElement | HTMLStyleElement>('link[rel="stylesheet"], style'))
    .map((element) => element.outerHTML)
    .join('\n');

  printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<base href="${escapeHtml(window.location.origin)}/">
<title>${escapeHtml(data.personal.fullName) || 'Resume'} - Resume</title>
${appStyles}
<style>
  ${buildSettingsCss(s)}
  ${LAYOUT_CSS[layout]}

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  @page { size: ${s.format}; margin: 0; }

  body {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    font-family: var(--resume-primary-font);
    background: white;
    color: #1e293b;
    font-size: var(--fs-body);
    line-height: var(--line-height);
  }

  /* Print buttons - hidden when printing */
  .print-buttons {
    display: flex;
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 9999;
    gap: 8px;
  }
  @media print {
    .print-buttons { display: none !important; }
  }

  /* Resume wrapper */
  .resume-wrapper {
    width: var(--resume-width);
    margin: 0 auto;
    background: white;
    min-height: var(--resume-height);
    overflow: hidden;
  }

  /* Name / header font size */
  .r-name { font-size: var(--fs-name); }
  .r-h2 { font-size: var(--fs-h2); }
  .r-h3 { font-size: var(--fs-h3); }
  .r-body { font-size: var(--fs-body); }
  .r-small { font-size: var(--fs-small); }
  .r-xs { font-size: var(--fs-xs); }

  /* Section spacing */
  .r-section { margin-bottom: var(--section-gap); }
  .r-section:last-child { margin-bottom: 0; }

  /* Header padding */
  .r-header { padding: var(--header-pad-y) var(--header-pad-x); }
  .r-content { padding: var(--content-pad); }

  /* Item gaps */
  .r-item-gap { gap: var(--item-gap); }
  .r-item-gap > * { margin-bottom: var(--item-gap); }
  .r-item-gap > *:last-child { margin-bottom: 0; }

  /* Skill tags */
  .r-skill {
    display: inline-flex;
    align-items: center;
    background: #334155;
    color: white;
    border-radius: 0.375rem;
    padding: var(--skill-pad);
    font-size: var(--fs-xs);
    font-weight: 500;
    white-space: nowrap;
  }

  /* Small tags */
  .r-tag {
    display: inline-block;
    background: #eff6ff;
    color: #1d4ed8;
    border-radius: 0.25rem;
    padding: var(--tag-pad);
    font-size: var(--fs-xs);
  }

  /* Utility */
  .r-flex { display: flex; }
  .r-flex-between { display: flex; justify-content: space-between; align-items: baseline; }
  .r-flex-wrap { display: flex; flex-wrap: wrap; }
  .r-block { display: block; }
  .r-w-full { width: 100%; }
  .r-text-slate-300 { color: #cbd5e1; }
  .r-text-slate-400 { color: #94a3b8; }
  .r-text-slate-500 { color: #64748b; }
  .r-text-slate-600 { color: #475569; }
  .r-text-slate-700 { color: #334155; }
  .r-text-slate-800 { color: #1e293b; }
  .r-text-slate-900 { color: #0f172a; }
  .r-text-white { color: white; }
  .r-text-blue-600 { color: #2563eb; }
  .r-text-blue-700 { color: #1d4ed8; }
  .r-text-indigo-600 { color: #4f46e5; }
  .r-bg-slate-50 { background: #f8fafc; }
  .r-bg-slate-100 { background: #f1f5f9; }
  .r-bg-slate-700 { background: #334155; }
  .r-bg-slate-800 { background: #1e293b; }
  .r-bg-slate-900 { background: #0f172a; }
  .r-bg-blue-50 { background: #eff6ff; }
  .r-bg-white { background: white; }
  .r-bg-indigo-50 { background: #eef2ff; }
  .r-border-slate-100 { border-color: #f1f5f9; }
  .r-border-slate-200 { border-color: #e2e8f0; }
  .r-border-slate-300 { border-color: #cbd5e1; }
  .r-border-b { border-bottom: 1px solid; }
  .r-border-b-2 { border-bottom: 2px solid; }
  .r-border-t { border-top: 1px solid; }
  .r-rounded { border-radius: 0.25rem; }
  .r-rounded-md { border-radius: 0.375rem; }
  .r-rounded-lg { border-radius: 0.5rem; }
  .r-rounded-full { border-radius: 9999px; }
  .r-font-bold { font-weight: 700; }
  .r-font-semibold { font-weight: 600; }
  .r-font-medium { font-weight: 500; }
  .r-font-normal { font-weight: 400; }
  .r-font-light { font-weight: 300; }
  .r-font-extralight { font-weight: 200; }
  .r-uppercase { text-transform: uppercase; }
  .r-italic { font-style: italic; }
  .r-underline { text-decoration: underline; }
  .r-whitespace-nowrap { white-space: nowrap; }
  .r-whitespace-pre-line { white-space: pre-line; }
  .r-list-disc { list-style-type: disc; padding-left: 1rem; }
  .r-grid { display: grid; }
  .r-grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
  .r-grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
  .r-text-center { text-align: center; }
  .r-text-right { text-align: right; }
  .r-text-justify { text-align: justify; }
  .r-mt-0 { margin-top: 0; }
  .r-mt-0\.5 { margin-top: 0.125rem; }
  .r-mt-1 { margin-top: 0.25rem; }
  .r-mt-2 { margin-top: 0.5rem; }
  .r-mt-3 { margin-top: 0.75rem; }
  .r-mt-4 { margin-top: 1rem; }
  .r-mt-5 { margin-top: 1.25rem; }
  .r-mt-6 { margin-top: 1.5rem; }
  .r-ml-1 { margin-left: 0.25rem; }
  .r-ml-1\.5 { margin-left: 0.375rem; }
  .r-ml-2 { margin-left: 0.5rem; }
  .r-mr-3 { margin-right: 0.75rem; }
  .r-mb-1 { margin-bottom: 0.25rem; }
  .r-mb-2 { margin-bottom: 0.5rem; }
  .r-mb-3 { margin-bottom: 0.75rem; }
  .r-mb-4 { margin-bottom: 1rem; }
  .r-mb-5 { margin-bottom: 1.25rem; }
  .r-mb-6 { margin-bottom: 1.5rem; }
  .r-p-2 { padding: 0.5rem; }
  .r-p-3 { padding: 0.75rem; }
  .r-p-4 { padding: 1rem; }
  .r-p-5 { padding: 1.25rem; }
  .r-px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
  .r-px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
  .r-py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
  .r-py-1\.5 { padding-top: 0.375rem; padding-bottom: 0.375rem; }
  .r-py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
  .r-pb-1 { padding-bottom: 0.25rem; }
  .r-pb-2 { padding-bottom: 0.5rem; }
  .r-pb-3 { padding-bottom: 0.75rem; }
  .r-pb-4 { padding-bottom: 1rem; }
  .r-pb-6 { padding-bottom: 1.5rem; }
  .r-pt-6 { padding-top: 1.5rem; }
  .r-pt-10 { padding-top: 2.5rem; }
  .r-pt-12 { padding-top: 3rem; }
  .r-gap-1 { gap: 0.25rem; }
  .r-gap-2 { gap: 0.5rem; }
  .r-gap-3 { gap: 0.75rem; }
  .r-gap-4 { gap: 1rem; }
  .r-gap-5 { gap: 1.25rem; }
  .r-gap-x-4 { column-gap: 1rem; }
  .r-gap-x-6 { column-gap: 1.5rem; }
  .r-gap-y-1 { row-gap: 0.25rem; }
  .r-gap-y-2 { row-gap: 0.5rem; }
  .r-gap-y-3 { row-gap: 0.75rem; }
  .r-space-y-1 > * + * { margin-top: 0.25rem; }
  .r-space-y-2 > * + * { margin-top: 0.5rem; }
  .r-space-y-3 > * + * { margin-top: 0.75rem; }
  .r-space-y-4 > * + * { margin-top: 1rem; }
  .r-space-y-6 > * + * { margin-top: 1.5rem; }
  .r-space-y-8 > * + * { margin-top: 2rem; }
  .r-space-y-10 > * + * { margin-top: 2.5rem; }
  .r-tracking-tight { letter-spacing: -0.025em; }
  .r-tracking-wide { letter-spacing: 0.025em; }
  .r-tracking-wider { letter-spacing: 0.05em; }
  .r-tracking-widest { letter-spacing: 0.1em; }

  ul.r-list-disc li { margin-bottom: 0.1rem; }
  ul.r-list-disc li:last-child { margin-bottom: 0; }
</style>
</head>
<body>
  <div class="print-buttons">
    <button onclick="window.print()" style="background:#2563eb;color:white;padding:8px 16px;border-radius:8px;border:none;cursor:pointer;font-size:14px;font-weight:500;">
      🖨️ Print / Save as PDF
    </button>
    <button onclick="window.close()" style="background:#e2e8f0;color:#334155;padding:8px 16px;border-radius:8px;border:none;cursor:pointer;font-size:14px;font-weight:500;">
      Close
    </button>
  </div>
  <div class="resume-wrapper">
    ${html}
  </div>
</body>
</html>`);

  printWindow.document.close();
}

function buildSettingsCss(settings: ResumeSettings): string {
  const width = settings.format === 'Letter' ? '215.9mm' : '210mm';
  const height = settings.format === 'Letter' ? '279.4mm' : '297mm';
  return `
    :root {
      --resume-width: ${width};
      --resume-height: ${height};
      --resume-margin-top: ${settings.margins.top}in;
      --resume-margin-bottom: ${settings.margins.bottom}in;
      --resume-margin-left: ${settings.margins.left}in;
      --resume-margin-right: ${settings.margins.right}in;
      --resume-section-gap: ${settings.spacing.sectionGap}px;
      --resume-title-gap: ${settings.spacing.titleGap}px;
      --resume-content-gap: ${settings.spacing.contentGap}px;
      --resume-line-height: ${settings.spacing.lineSpacing};
      --resume-primary-font: ${settings.fonts.primary}, system-ui, sans-serif;
      --resume-secondary-font: ${settings.fonts.secondary}, system-ui, sans-serif;
      --resume-heading-scale: ${settings.sizes.heading / 100};
      --resume-body-scale: ${settings.sizes.body / 100};
    }
  `;
}

function escapeHtml(text: string): string {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
